import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { BiLeftArrowAlt, BiRightArrowAlt } from "react-icons/bi";
import { usePostHog } from "posthog-js/react";
// import panda from "../../../assets/panda.png";
import panda2 from "../../../assets/Panda2.png";
import { usePopupWindowCloseHandler } from "../../../utils/useWindowCloseHandler.js";

const FocusPopup = ({
  summary,
  options,
  alternatePerspective,
  backstory,
  onClose,
  newSession,
  userId,
  apiBaseURL,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const posthog = usePostHog();
  const totalSteps = 3;

  usePopupWindowCloseHandler(newSession, userId, apiBaseURL, true);

  useEffect(() => {
    posthog.capture("Focus_popup", { focus: "what u want to focus on" });
  }, [posthog]);

  const handleOptionSelect = useCallback((option) => {
    setSelectedOption(option);
    setInputValue("");
  }, []);

  const handleInputChange = useCallback((e) => {
    setInputValue(e.target.value);
    setSelectedOption(null);
  }, []);

  const handleNavigation = (index) => {
    if (index >= 0 && index < totalSteps) setCurrentIndex(index);
  };

  const handleSubmit = () => {
    const finalSelection = inputValue || selectedOption;
    if (!finalSelection) return;
    setSubmitting(true);
    onClose(finalSelection);
    setTimeout(() => setSubmitting(false), 300); // arbitrary delay to simulate feedback
  };

  const Components = useMemo(
    () => [
      <CoachPerspective summary={summary} />,
      <AlternatePerspective alternatePerspective={alternatePerspective} />,
      <Explore
        options={options}
        backstory={backstory}
        selectedOption={selectedOption}
        inputValue={inputValue}
        handleOptionSelect={handleOptionSelect}
        handleInputChange={handleInputChange}
      />,
    ],
    [
      summary,
      alternatePerspective,
      options,
      backstory,
      selectedOption,
      inputValue,
      handleOptionSelect,
      handleInputChange,
    ]
  );

  const isFinalStep = currentIndex === totalSteps - 1;
  const isSubmitEnabled = !!(selectedOption || inputValue);

  return (
    <div className="fixed inset-0 bg-gray-700 bg-opacity-80 flex justify-center items-center z-50 font-gotham">
      <div className="bg-white mt-10 rounded-2xl shadow-lg w-[80%] md:w-[85%] lg:w-[70%] xl:w-[65%] lg:max-w-[900px] xl:max-w-[1000px] overflow-hidden">
        {/* Header Dots */}
        <div className="flex gap-3 ml-4 pt-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="w-4 h-4 rounded-full"></div>
          ))}
        </div>

        <div className="mt-2 h-[550px] md:h-[450px] lg:h-[550px]">
          {Components[currentIndex]}
        </div>

        <div className="flex justify-between items-center mt-8 px-6 pb-3">
          <div className="flex gap-2 md:gap-4">
            {[...Array(totalSteps)].map((_, i) => (
              <span
                key={i}
                className={`w-4 h-4 rounded-full cursor-pointer ${
                  i === currentIndex ? "bg-[#54B0AF]" : "bg-gray-300"
                }`}
                onClick={() => handleNavigation(i)}
              />
            ))}
          </div>

          <div className="flex gap-3 items-center justify-center">
            {currentIndex > 0 && (
              <button
                className="px-4 py-1 text-3xl rounded-3xl bg-[#F5F5F5] hover:bg-[#54B0AF] text-[#484848] hover:text-white flex items-center justify-center"
                onClick={() => handleNavigation(currentIndex - 1)}
              >
                <BiLeftArrowAlt />
              </button>
            )}
            {!isFinalStep ? (
              <button
                className="px-4 py-1 text-3xl rounded-3xl bg-[#F5F5F5] hover:bg-[#54B0AF] hover:text-white text-[#484848] flex items-center justify-center"
                onClick={() => handleNavigation(currentIndex + 1)}
              >
                <BiRightArrowAlt />
              </button>
            ) : (
              <motion.button
                whileHover={{ scale: isSubmitEnabled ? 1.1 : 1 }}
                transition={{ duration: 0.2 }}
                disabled={!isSubmitEnabled || submitting}
                onClick={handleSubmit}
                className={`px-6 py-1 text-lg font-medium rounded-3xl transition ${
                  isSubmitEnabled
                    ? "bg-[#54B0AF] text-white hover:bg-[#54B0AF]"
                    : "bg-[#F5F5F5] text-bg-gray-400 cursor-not-allowed"
                }`}
              >
                {submitting ? "Submitting..." : "Submit"}
              </motion.button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CoachPerspective = ({ summary }) => {
  return (
    <div>
      {/* <div className="relative w-full bg-[#E3E3E3] mt-4 h-[15rem] md:h-[20rem] xl:h-[25rem] overflow-hidden">
        <motion.img
          src={panda2}
          alt="Panda"
          className="absolute bottom-0 right-0 h-[80%] md:h-full object-bottom object-contain"
          initial={{
            scale: 1.5,
            x: -50,
            scaleX: 1,
          }}
          animate={{
            scale: 1,
            x: 0,
            scaleX: 1,
          }}
          transition={{ duration: 0.5 }}
        />
      </div> */}
      <div className="relative w-full bg-[#E3E3E3] mt-4 h-[240px] md:h-[320px] lg:h-[320px] overflow-hidden">
        <motion.img
          src={panda2}
          alt="Panda"
          className="absolute bottom-0 right-0 h-[90%] md:h-[90%] lg:h-full object-bottom object-contain"
          initial={{
            scale: 1.5,
            x: -50,
            scaleX: 1,
          }}
          animate={{
            scale: 1,
            x: 0,
            scaleX: 1,
          }}
          transition={{ duration: 0.5 }}
        />
      </div>

      <div className="pt-8 md:pt-4 p-2 font-sans text-left">
        <h2 className="text-xl leading-5 md:leading-normal md:text-2xl lg:text-3xl font-gotham  text-[#323232] font-[700]">
          Here’s what you’ve uncovered so far.
        </h2>
        <p className="text-[#323232] mt-1 md:mt-2 text-lg md:text-xl lg:text-2xl font-gotham font-[400]">
          {summary}
        </p>
      </div>
    </div>
  );
};

const AlternatePerspective = ({ alternatePerspective }) => {
  return (
    <div>
      <div className="relative w-full bg-gray-200 mt-4 h-[240px] md:h-[320px] lg:h-[320px] overflow-hidden">
        <motion.img
          src={panda2}
          alt="Panda"
          className="absolute bottom-0 right-0 h-[90%] md:h-full object-bottom object-contain"
          initial={{
            scale: 1.5,
            x: -50,
            scaleX: 1,
          }}
          animate={{
            scale: 1,
            x: 0,
            scaleX: 1,
          }}
          transition={{ duration: 0.5 }}
        />
      </div>

      <div className="pt-8 md:pt-4 p-2 font-sans text-left">
        <h2 className="text-xl leading-5 md:leading-normal md:text-2xl lg:text-3xl font-gotham  text-[#323232] font-[700]">
          Consider this alternate perspective.
        </h2>
        <p className="text-[#323232] mt-1 md:mt-2 text-lg md:text-xl lg:text-2xl font-gotham font-[400]">
          {alternatePerspective}
        </p>
      </div>
    </div>
  );
};

const Explore = ({
  options,
  backstory,
  selectedOption,
  inputValue,
  handleOptionSelect,
  handleInputChange,
}) => {
  return (
    <div>
      <motion.div
        className="bg-[#F5F5F5] flex justify-between w-full h-[5rem] md:h-[10rem] lg:h-[380px] xl:h-[400px]"
        initial={{ height: "40%" }}
        animate={{ height: "30%" }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex relative">
          <h1 className="text-[#323232] top-[50%] text-lg md:text-xl lg:text-2xl pl-3 md:pl-10 font-semibold font-gotham flex items-center justify-center leading-5 md:w-[100%] w-[80%]">
            Which path do you want to explore further?
          </h1>
        </div>
        <div className="flex justify-end lg:h-[238px] ">
          <motion.img
            src={panda2}
            alt=""
            className="h-full object-cover w-full"
            initial={{
              scale: 1.5,
              height: "70%",
              x: -50,
              scaleX: 1,
            }}
            animate={{
              scale: 1,
              height: "100%",
              x: 0,
              scaleX: 1,
            }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </motion.div>

      <div className="mt-2 h-[400px] md:h-auto lg:h-auto overflow-y-auto">
        <div className="md:mt-1 h-full">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 p-4 h-full overflow-y-auto">
            {options.map((option, index) => (
              <div
                key={index}
                className={`rounded-xl relative flex flex-col p-4 pt-4 md:pt-5 lg:pt-6 hover:shadow-md transition cursor-pointer ${
                  selectedOption === option
                    ? "border border-[#54B0AF] text-white"
                    : "border border-gray-400"
                }`}
                onClick={() => handleOptionSelect(option)}
              >
                <input
                  type="radio"
                  name="choice"
                  checked={selectedOption === option}
                  onChange={() => handleOptionSelect(option)}
                  className="appearance-none w-4 h-4 md:w-6 md:h-6 rounded-full border-4 border-white ring-2 ring-[#54B0AF] checked:bg-[#54B0AF] transition duration-300 cursor-pointer absolute top-4 left-4"
                />
                <label className="text-sm md:text-base lg:text-lg font-medium font-gotham text-[#484848] cursor-pointer mt-5 md:mt-6">
                  {option}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>

      <textarea
        rows={3}
        placeholder="Something else? Please type here..."
        value={inputValue}
        onChange={handleInputChange}
        className="md:mt-4 mt-2 w-full md:p-2 mx-4 overflow-y-auto rounded-lg font-gotham outline-none focus:outline-none text-base md:text-lg lg:text-xl text-[#484848] font-[400]"
      />
    </div>
  );
};

export default FocusPopup;

// before 01 Aug 2025

// import React, { useState, useEffect, useCallback, useMemo } from "react";
// import { motion } from "framer-motion";
// import { BiLeftArrowAlt, BiRightArrowAlt } from "react-icons/bi";
// import { usePostHog } from "posthog-js/react";
// import panda from "../../../assets/panda.png";
// import panda2 from "../../../assets/Panda2.png";

// const FocusPopup = ({
//   summary,
//   options,
//   alternatePerspective,
//   backstory,
//   onClose,
// }) => {
//   const [currentIndex, setCurrentIndex] = useState(0);
//   const [selectedOption, setSelectedOption] = useState(null);
//   const [inputValue, setInputValue] = useState("");
//   const [submitting, setSubmitting] = useState(false);

//   const posthog = usePostHog();
//   const totalSteps = 3;

//   useEffect(() => {
//     posthog.capture("Focus_popup", { focus: "what u want to focus on" });
//   }, [posthog]);

//   const handleOptionSelect = useCallback((option) => {
//     setSelectedOption(option);
//     setInputValue("");
//   }, []);

//   const handleInputChange = useCallback((e) => {
//     setInputValue(e.target.value);
//     setSelectedOption(null);
//   }, []);

//   const handleNavigation = (index) => {
//     if (index >= 0 && index < totalSteps) setCurrentIndex(index);
//   };

//   const handleSubmit = () => {
//     const finalSelection = inputValue || selectedOption;
//     if (!finalSelection) return;
//     setSubmitting(true);
//     onClose(finalSelection);
//     setTimeout(() => setSubmitting(false), 300); // arbitrary delay to simulate feedback
//   };

//   const Components = useMemo(
//     () => [
//       <CoachPerspective summary={summary} />,
//       <AlternatePerspective alternatePerspective={alternatePerspective} />,
//       <Explore
//         options={options}
//         backstory={backstory}
//         selectedOption={selectedOption}
//         inputValue={inputValue}
//         handleOptionSelect={handleOptionSelect}
//         handleInputChange={handleInputChange}
//       />,
//     ],
//     [
//       summary,
//       alternatePerspective,
//       options,
//       backstory,
//       selectedOption,
//       inputValue,
//       handleOptionSelect,
//       handleInputChange,
//     ]
//   );

//   const isFinalStep = currentIndex === totalSteps - 1;
//   const isSubmitEnabled = !!(selectedOption || inputValue);

//   return (
//     <div className="fixed inset-0 bg-gray-700 bg-opacity-80 flex justify-center items-center z-50 font-gotham">
//       <div className="bg-white mt-10 rounded-2xl shadow-lg w-[80%] md:w-[85%] lg:w-[70%] xl:w-[65%] max-w-[1000px] overflow-hidden">
//         {/* Header Dots */}
//         <div className="flex gap-3 ml-4 pt-2">
//           {[...Array(3)].map((_, i) => (
//             <div key={i} className="w-4 h-4 rounded-full"></div>
//           ))}
//         </div>

//         <div className="mt-2 h-[550px] md:h-[450px] lg:h-[550px]">
//           {Components[currentIndex]}
//         </div>

//         <div className="flex justify-between items-center mt-8 px-6 pb-3">
//           <div className="flex gap-2 md:gap-4">
//             {[...Array(totalSteps)].map((_, i) => (
//               <span
//                 key={i}
//                 className={`w-4 h-4 rounded-full cursor-pointer ${
//                   i === currentIndex ? "bg-[#54B0AF]" : "bg-gray-300"
//                 }`}
//                 onClick={() => handleNavigation(i)}
//               />
//             ))}
//           </div>

//           <div className="flex gap-3 items-center justify-center">
//             {currentIndex > 0 && (
//               <button
//                 className="px-4 py-1 text-3xl rounded-3xl bg-[#F5F5F5] hover:bg-[#54B0AF] text-[#484848] hover:text-white flex items-center justify-center"
//                 onClick={() => handleNavigation(currentIndex - 1)}
//               >
//                 <BiLeftArrowAlt />
//               </button>
//             )}
//             {!isFinalStep ? (
//               <button
//                 className="px-4 py-1 text-3xl rounded-3xl bg-[#F5F5F5] hover:bg-[#54B0AF] hover:text-white text-[#484848] flex items-center justify-center"
//                 onClick={() => handleNavigation(currentIndex + 1)}
//               >
//                 <BiRightArrowAlt />
//               </button>
//             ) : (
//               <motion.button
//                 whileHover={{ scale: isSubmitEnabled ? 1.1 : 1 }}
//                 transition={{ duration: 0.2 }}
//                 disabled={!isSubmitEnabled || submitting}
//                 onClick={handleSubmit}
//                 className={`px-6 py-1 text-lg font-medium rounded-3xl transition ${
//                   isSubmitEnabled
//                     ? "bg-[#54B0AF] text-white hover:bg-[#54B0AF]"
//                     : "bg-[#F5F5F5] text-bg-gray-400 cursor-not-allowed"
//                 }`}
//               >
//                 {submitting ? "Submitting..." : "Submit"}
//               </motion.button>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// const CoachPerspective = ({ summary }) => {
//   return (
//     <div>
//       <div className="relative w-full bg-gray-200 mt-4 h-[15rem] md:h-[20rem] lg:h-[25rem] overflow-hidden">
//         <motion.img
//           src={panda2}
//           alt="Panda"
//           className="absolute bottom-0 right-0 h-[90%] md:h-full object-bottom object-contain"
//           initial={{
//             scale: 1.5,
//             x: -50,
//             scaleX: 1,
//           }}
//           animate={{
//             scale: 1,
//             x: 0,
//             scaleX: 1,
//           }}
//           transition={{ duration: 0.5 }}
//         />
//       </div>

//       <div className="pt-8 md:pt-4 p-2 font-sans text-left">
//         <h2 className="text-xl leading-5 md:leading-normal md:text-2xl lg:text-3xl font-gotham  text-[#323232] font-[700]">
//           Here’s how this Knot has been untying till now.
//         </h2>
//         <p className="text-[#323232] mt-1 md:mt-2 text-lg md:text-xl lg:text-2xl font-gotham font-[400]">
//           {summary}
//         </p>
//       </div>
//     </div>
//   );
// };

// const AlternatePerspective = ({ alternatePerspective }) => {
//   return (
//     <div>
//       <div className="relative w-full bg-gray-200 mt-4 h-[15rem] md:h-[20rem] lg:h-[25rem] overflow-hidden">
//         <motion.img
//           src={panda2}
//           alt="Panda"
//           className="absolute bottom-0 right-0 h-[90%] md:h-full object-bottom object-contain"
//           initial={{
//             scale: 1.5,
//             x: -50,
//             scaleX: 1,
//           }}
//           animate={{
//             scale: 1,
//             x: 0,
//             scaleX: 1,
//           }}
//           transition={{ duration: 0.5 }}
//         />
//       </div>

//       <div className="pt-8 md:pt-4 p-2 font-sans text-left">
//         <h2 className="text-xl leading-5 md:leading-normal md:text-2xl lg:text-3xl font-gotham  text-[#323232] font-[700]">
//           Consider this alternate perspective.
//         </h2>
//         <p className="text-[#323232] mt-1 md:mt-2 text-lg md:text-xl lg:text-2xl font-gotham font-[400]">
//           {alternatePerspective}
//         </p>
//       </div>
//     </div>
//   );
// };

// const Explore = ({
//   options,
//   backstory,
//   selectedOption,
//   inputValue,
//   handleOptionSelect,
//   handleInputChange,
// }) => {
//   return (
//     <div>
//       <motion.div
//         className="bg-[#F5F5F5] flex justify-between w-full h-[5rem] md:h-[10rem] lg:h-[15rem]"
//         initial={{ height: "40%" }}
//         animate={{ height: "30%" }}
//         transition={{ duration: 0.5 }}
//       >
//         <div className="flex relative">
//           <h1 className="text-[#323232] top-[50%] text-lg md:text-xl lg:text-2xl pl-3 md:pl-10 font-semibold font-gotham flex items-center justify-center leading-5 md:w-[100%] w-[80%]">
//             Which path do you want to explore further?
//           </h1>
//         </div>
//         <div className="flex justify-end lg:h-[238px]">
//           <motion.img
//             src={panda2}
//             alt=""
//             className="h-full object-cover w-full"
//             initial={{
//               scale: 1.5,
//               height: "70%",
//               x: -50,
//               scaleX: 1,
//             }}
//             animate={{
//               scale: 1,
//               height: "100%",
//               x: 0,
//               scaleX: 1,
//             }}
//             transition={{ duration: 0.5 }}
//           />
//         </div>
//       </motion.div>

//       <div className="mt-2 h-[400px] md:h-auto lg:h-auto overflow-y-auto">
//         <div className="md:mt-1 h-full">
//           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 p-4 h-full overflow-y-auto">
//             {options.map((option, index) => (
//               <div
//                 key={index}
//                 className={`rounded-xl relative flex flex-col p-4 pt-4 md:pt-5 lg:pt-6 hover:shadow-md transition cursor-pointer ${
//                   selectedOption === option
//                     ? "border border-[#54B0AF] text-white"
//                     : "border border-gray-400"
//                 }`}
//                 onClick={() => handleOptionSelect(option)}
//               >
//                 <input
//                   type="radio"
//                   name="choice"
//                   checked={selectedOption === option}
//                   onChange={() => handleOptionSelect(option)}
//                   className="appearance-none w-4 h-4 md:w-6 md:h-6 rounded-full border-4 border-white ring-2 ring-[#54B0AF] checked:bg-[#54B0AF] transition duration-300 cursor-pointer absolute top-4 left-4"
//                 />
//                 <label className="text-sm md:text-base lg:text-lg font-medium font-gotham text-[#484848] cursor-pointer mt-5 md:mt-6">
//                   {option}
//                 </label>
//               </div>
//             ))}
//           </div>
//         </div>
//       </div>

//       <textarea
//         rows={3}
//         placeholder="Something else? Please type here..."
//         value={inputValue}
//         onChange={handleInputChange}
//         className="md:mt-4 mt-2 w-full md:p-2 mx-4 overflow-y-auto rounded-lg font-gotham outline-none focus:outline-none text-base md:text-lg lg:text-xl text-[#484848] font-[400]"
//       />
//     </div>
//   );
// };

// export default FocusPopup;
